import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { FormControlLabel, Checkbox, makeStyles } from "@material-ui/core";
import { useEventListener, useUpdateEffect } from "ahooks";
import clsx from "clsx";
import { language } from "@/helpers/datatableLanguage";
import { useTranslation } from "@/langs/useTranslation";
import Pagination from "@/components/Pagination";
import $ from "jquery";
import DataTableNet from "datatables.net";
import "datatables.net-select-dt";
import "jquery-datatables-checkboxes";
// import "datatables-rowsgroup-umd";
import RowsGroupPlugin from "./rowsGroup";
import "@/css/dataTable.css";
import { isPromise } from "@/helpers/isPromise";
import { errorHandling } from "@/helpers/errorHandling";
import { getToken } from "@/apis/getToken";

const useStyles = makeStyles(theme => ({
  dataTableWrap: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  withSearching: {
    marginTop: -54
  },
  hideFilter: {
    "& .dataTables_filter": {
      display: "none !important"
    }
  },
  dataTable: {
    height: "100%",
    overflowX: "auto",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    webkitOverflowScrolling: "touch",
    // paddingTop: theme.spacing(2),
    // paddingBottom: theme.spacing(2),

    "& div.dataTables_wrapper": {
      position: "static",
      paddingBottom: 0
    },

    "& .dataTables_paginate": {
      display: "none !important"
    },

    "& .dataTable .dt-checkboxes-cell::after, & .dataTable .sticky::after": {
      position: "absolute",
      top: 0,
      bottom: -1,
      width: 30,
      transition: "box-shadow 0.3s",
      content: '""',
      pointerEvents: "none",
      opacity: 1
    },
    "& .dataTable .dt-checkboxes-cell::after, & .dataTable .sticky.left::after":
      {
        right: "0 !important",
        transform: "translate(100%)"
      },
    "&.fixed-left .dataTable .dt-checkboxes-cell::after, &.fixed-left .dataTable .sticky.left::after":
      {
        boxShadow: "inset 10px 0 8px -8px #00000026"
      },
    "& .dataTable .sticky.right::after": {
      left: "0 !important",
      transform: "translate(-100%)"
    },
    "&.fixed-right .dataTable .sticky.right::after": {
      boxShadow: "inset -10px 0 8px -8px #00000026"
    }
  },
  tableFooter: {
    display: "flex",
    justifyContent: "space-between",
    // minHeight: 57.5,
    minHeight: 35.5,
    marginTop: theme.spacing(2)
  },
  tableFooterLeft: {
    "& .MuiButton-root": {
      marginLeft: theme.spacing(2)
    }
  },
  scrollable: {
    height: "100%",
    minHeight: 0,

    "& $dataTable": {
      flex: 1,
      overflow: "auto"
      // overflow: "overlay"
    },

    "& .dataTable > thead": {
      position: "sticky",
      top: 0
    }
  },
  rowClickable: {
    "& .dataTable > tbody > tr": {
      cursor: "pointer"
    }
  }
}));

let drawCount = 0;

export function getDraw() {
  return ++drawCount;
}

const DataTable = React.forwardRef(
  (
    {
      id,
      selectProp,
      className,
      style,
      autoLoad,
      paging = true,
      showCheckAll = true,
      showFooter = true,
      showFilter,
      rerenderOnLanguageChange = true,
      scrollable,
      ordering,
      defaultOrder,
      searching,
      singleCheck,
      checkAllText,
      checkAllDisabled,
      ajax,
      fnServerData,
      columns,
      rowsGroup,
      data,
      options: opts,
      footer,
      children,
      onCheckAll,
      onSelectChange,
      onPageChange,
      onLengthChange,
      onPreXhr,
      onXhr,
      onOrder,
      onDraw,
      onLoaded,
      onRowClick
    },
    ref
  ) => {
    const dataTableRef = useRef(null);
    const tableRef = useRef(null);
    const tableInstRef = useRef(null);
    const [checkedAll, setCheckedAll] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const classes = useStyles();
    const jqXHR = useRef(null);
    const lastPage = useRef(0);

    const { t, userLanguage } = useTranslation("device");

    columns = columns || opts.columns;

    const _selectProp = selectProp
      ? selectProp
      : columns &&
        columns[0] &&
        columns[0].type === "checkbox" &&
        columns[0].data;

    const defaultOptions = {
      serverSide: true,
      processing: true,
      language: language(t),
      buttons: [],
      searching: false,
      select: {
        style: singleCheck ? "single" : "multi"
      },
      info: false,
      // deferRender: true,
      // retrieve: true,
      autoWidth: false,
      stateSave: false,
      cache: false,
      destroy: true,
      ordering: false,
      lengthChange: false,
      paging: true,
      pageLength: 20,
      pagingType: "simple" // simple,simple_numbers,full,full_numbers
      // lengthMenu: [10, 20, 50, 100, 200],
      // filter: false
    };

    const handleCheckAll = checked => {
      setCheckedAll(checked);
      onCheckAll?.(checked);
    };

    useImperativeHandle(ref, () => ({
      api: () => tableInstRef.current,
      selectAll: state => {
        tableInstRef.current?.column(0).checkboxes.select(state);
      },
      setSelected: ids => {
        const api = tableInstRef.current;
        const data = api.data().toArray();
        api
          .cells(
            api
              .rows()
              .indexes()
              .filter(index => ids.includes(data[index][_selectProp])),
            0
          )
          .checkboxes.select(true);
      },
      disabledAll: state => {
        try {
          const api = tableInstRef.current;
          api.column(0).checkboxes.select(false);
          api.cells().checkboxes.disable(state);
          $(api.table().header())
            .find(".dt-checkboxes-select-all input[type=checkbox]")
            .prop("disabled", state);
        } catch (e) {
          console.error(e);
        }
      },
      setDisabled(ids, state) {
        const api = tableInstRef.current;
        const data = api.data().toArray();
        api
          .cells(
            api
              .rows()
              .indexes()
              .filter(index => ids.includes(data[index][_selectProp])),
            0
          )
          .checkboxes.disable(state);
      },
      getAll: () => {
        return tableInstRef.current?.data().toArray();
      },
      getSelected: () => {
        return tableInstRef.current?.column(0).checkboxes.selected().toArray();
      },
      getSelectedRows: () => {
        const selectedRowIds = tableInstRef.current
          ?.column(0)
          .checkboxes.selected()
          .toArray();
        // const selectedRows = tableInstRef.current
        //   ?.data()
        //   .filter(item => selectedRowIds.includes(item[_selectProp]))
        //   .toArray();
        const data = tableInstRef.current?.data().toArray();
        const selectedRows = selectedRowIds
          .map(id => data.find(item => item[_selectProp] === id))
          .filter(Boolean);
        return selectedRows;
      },
      reload: () => {
        // if (loaded) {
        //   tableInstRef.current.ajax.reload();
        // } else {
        initTable();
        // }
      }
    }));

    const handleToggleCheckAll = e => {
      setCheckedAll(e.target.checked);
      tableInstRef.current?.column(0).checkboxes.select(e.target.checked);
    };

    const destroyTable = remove => {
      if (tableInstRef.current) {
        tableInstRef.current.off(".dt");
        tableInstRef.current.destroy(remove);
        const checkboxesPlugin = tableInstRef.current.settings()[0].checkboxes;
        if (checkboxesPlugin) {
          checkboxesPlugin.onDataTablesDestroy();
        }
      }
    };

    const abortLastRequest = () => {
      const xhr = jqXHR.current;
      if (xhr && xhr.readyState !== 4) {
        if (xhr.abort) {
          xhr.abort();
        } else if (isPromise(xhr)) {
          xhr.then(r => r && r.abort && r.abort());
        }
      }
    };

    const initTable = () => {
      const options = { ...opts };

      if (data) {
        options.data = data;
      }

      if (ajax) {
        options.ajax = ajax;
      }

      if (defaultOrder) {
        options.order = defaultOrder;
      }

      if (ordering !== undefined) {
        options.ordering = ordering;
      }

      if (searching !== undefined) {
        options.searching = searching;
      }

      if (fnServerData) {
        options.fnServerData = fnServerData;
      }

      if (columns) {
        options.columns = columns;
      }

      if (rowsGroup) {
        options.rowsGroup = rowsGroup;
      }

      if (!options.columnDefs) {
        options.columnDefs = [];
      }

      if (
        options.columns &&
        options.columns[0] &&
        options.columns[0].type === "checkbox"
      ) {
        options.columnDefs.unshift({
          targets: 0,
          searchable: false,
          orderable: false,
          checkboxes: {
            selectRow: singleCheck ? true : false, // true
            selectAll: singleCheck ? false : undefined
          }
        });
      }

      const origInitComplete = options.initComplete;
      const origAjax = options.ajax;
      const origFnServerData = options.fnServerData;

      // setLoaded(false);
      setCheckedAll(false);

      options.initComplete = function (settings, json) {
        // const api = settings.checkboxes.s.dt;
        const api = this.api();
        origInitComplete?.(settings, json, api);
        if (!loaded) {
          setLoaded(true);
        }
        setLoading(false);
        onLoaded?.(json, settings, api);
        // api.cells().checkboxes.disable(true);
        api
          .on("draw.dt", () => {
            setCheckedAll(false);
            setLoading(false);
            onDraw?.();
          })
          .on("page.dt", () => {
            lastPage.current = api.page.info().page;
            onPageChange?.(api.page.info());
          })
          .on("length.dt", () => {
            onLengthChange?.(api.page.info());
          })
          .on("order.dt", (e, settings, data, order) => {
            onOrder?.(settings, data, order);
            const pageInfo = api.page.info();
            if (pageInfo.page !== lastPage.current) {
              onPageChange?.(pageInfo);
            }
            lastPage.current = pageInfo.page;
          })
          .on("change.dt", "input[type=checkbox]", function (e) {
            // console.log(api.row(0).column(0).data(), '??')
            // api.column(0).checkboxes.deselectAll()
            const selectedRowIds = api
              .column(0)
              .checkboxes.selected()
              .toArray();
            if (this.checked) {
              let isAll = false;
              if (!rowsGroup) {
                isAll =
                  selectedRowIds.length === json.data.length ||
                  selectedRowIds.length === api.page.info().recordsTotal ||
                  selectedRowIds.length === api.page.len();
              } else {
                isAll = selectedRowIds.length === json.data.length;
              }
              if (isAll) {
                handleCheckAll(true);
              } else {
                handleCheckAll(false);
              }
            } else {
              handleCheckAll(false);
            }
            const selectedRows = api
              .data()
              .filter(item => selectedRowIds.includes(item[_selectProp]))
              .toArray();
            onSelectChange?.(selectedRowIds, selectedRows);
          });

        if (onRowClick) {
          api.on("click.dt", "tbody tr", function (e) {
            // jquery和react事件无法统一只能暂时这样来阻止按钮触发这个点击事件
            if (
              (e.target.className &&
                e.target.className.indexOf &&
                e.target.className.indexOf("MuiButton") >= 0) ||
              $(e.target).data("disablerowclick")
            ) {
              return;
            }
            onRowClick(e, api.row(this).data());
          });
        }
      };

      if (typeof origAjax === "function") {
        options.ajax = (data, callback, settings) => {
          let pageSize, page, draw;
          if (settings.oInit.serverSide) {
            pageSize =
              data.length === -1 ? defaultOptions.pageLength : data.length;
            page = data.start / pageSize + 1;
            draw = data.draw;
          } else {
            pageSize = 1000000; // 200
            page = 1;
            draw = getDraw();
          }
          data.draw = draw;
          data.page = page;
          data.pageSize = pageSize;
          return origAjax(data, callback, settings);
        };
      } else if (typeof origAjax === "object") {
        if (!origAjax.beforeSend) {
          origAjax.beforeSend = request => {
            request.setRequestHeader("Authorization", "Bearer " + getToken());
            request.setRequestHeader("Accept", "application/json");
          };
        }
        if (!origAjax.error) {
          origAjax.error = err => {
            errorHandling(err);
          };
        }
      }

      if (typeof origFnServerData === "function") {
        options.fnServerData = (ajaxSource, data, callback, settings) => {
          const oAjaxData = settings.oAjaxData;
          let pageSize, page, draw;
          if (settings.oInit.serverSide) {
            pageSize =
              oAjaxData.length === -1
                ? defaultOptions.pageLength
                : oAjaxData.length;
            page = oAjaxData.start / pageSize + 1;
            draw = oAjaxData.draw;
          } else {
            pageSize = 1000000;
            page = 1;
            draw = getDraw();
          }
          oAjaxData.draw = draw;
          oAjaxData.page = page;
          oAjaxData.pageSize = pageSize;
          return origFnServerData(oAjaxData, callback, settings);
        };
      }

      options.columns = options.columns.map(column => {
        if (!column.name) {
          // for operating column
          if (column.data === null) {
            return {
              ...column,
              orderable: column.orderable ?? false,
              searchable: column.searchable ?? false,
              name: "unknown",
              className: ["sticky right operation", column.className].join(" ")
            };
          }
          if (column.data && column.data !== "function") {
            return {
              ...column,
              name: column.data
            };
          }
        } else if (column.name === "orderNo" && !column.data) {
          return {
            data: (rowData, type, s, meta) => {
              if (!meta) return "";
              const {
                row,
                settings: { oAjaxData }
              } = meta;
              const page = oAjaxData.start / oAjaxData.length;
              return oAjaxData.length * page + row + 1;
            },
            searchable: false,
            orderable: false,
            ...column,
            className: ["sticky left orderNo", column.className].join(" ")
          };
        }
        return column;
      });

      abortLastRequest();
      destroyTable();

      // DataTableNet.ext.errMode = "throw";
      tableInstRef.current = new DataTableNet(tableRef.current, {
        ...defaultOptions,
        ...options
      });

      RowsGroupPlugin.init(tableInstRef.current, rowsGroup);
    };

    useEffect(() => {
      if (data) {
        const api = tableInstRef.current;
        api?.clear();
        api?.rows.add(data).draw(false);
      }
    }, [data]);

    useEffect(() => {
      if (autoLoad) {
        initTable();
      }
    }, [autoLoad]); // eslint-disable-line

    useUpdateEffect(() => {
      if (rerenderOnLanguageChange) {
        // tableInstRef.current?.draw("page");
        initTable();
      }
    }, [userLanguage, rerenderOnLanguageChange]);

    useLayoutEffect(() => {
      const $tbWrap = $(dataTableRef.current)
        .on("preXhr.dt.xhr", (e, settings, data) => {
          setLoading(true);
          Promise.resolve().then(() => {
            jqXHR.current = settings.jqXHR;
            onPreXhr?.(settings, data);
          });
        })
        .on("xhr.dt.xhr", (e, settings, json, xhr) => {
          jqXHR.current = xhr;
          onXhr?.(settings, json, xhr);
        })
        .on("draw.dt.xhr", () => {
          const ele = dataTableRef.current;
          if ((ele.scrollLeft + ele.offsetWidth) >> 0 < ele.scrollWidth >> 0) {
            ele.classList.add("fixed-right");
          }
        });

      return () => {
        $tbWrap.off(".dt.xhr");
        abortLastRequest();
        destroyTable(true);
      };
    }, []); // eslint-disable-line

    useEventListener(
      "scroll",
      () => {
        const ele = dataTableRef.current;
        if (ele.scrollLeft > 0) {
          ele.classList.add("fixed-left");
        } else {
          ele.classList.remove("fixed-left");
        }
        if ((ele.scrollLeft + ele.offsetWidth) >> 0 < ele.scrollWidth >> 0) {
          ele.classList.add("fixed-right");
        } else {
          ele.classList.remove("fixed-right");
        }
      },
      { target: dataTableRef, passive: true }
    );

    return (
      <div
        className={clsx(
          "datatable",
          classes.dataTableWrap,
          searching && classes.withSearching,
          showFilter === false && classes.hideFilter,
          scrollable && classes.scrollable,
          !!onRowClick && classes.rowClickable,
          className
        )}
        style={style}
      >
        <div className={classes.dataTable} ref={dataTableRef}>
          <table
            ref={tableRef}
            id={id}
            className={`mdl-data-table no-pagination${
              rowsGroup ? " hasRowsGroup" : ""
            }`}
          >
            {children}
          </table>
        </div>
        {loaded && showFooter && (
          <div
            className={classes.tableFooter}
            style={{ pointerEvents: !loaded || loading ? "none" : "" }}
          >
            <div className={classes.tableFooterLeft}>
              {showCheckAll && (
                <FormControlLabel
                  style={{ marginLeft: 5 }}
                  disabled={checkAllDisabled}
                  control={
                    <Checkbox
                      checked={checkedAll}
                      onChange={handleToggleCheckAll}
                    />
                  }
                  label={checkAllText || t({ defaultMessage: "全选" })}
                />
              )}
              {footer}
            </div>
            {paging && !loading && <Pagination table={tableInstRef} />}
          </div>
        )}
      </div>
    );
  }
);

export default DataTable;
